import { Box, Button, Divider, FormHelperText, TextField, Typography } from '@mui/material';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { useAtomValue, useSetAtom } from 'jotai';
import { User_Role_Enum, UserRoleEnum } from '@kheops/graphql';
import { useInsertUserMutation } from '../../mutations/__generated__/insertUser.generated';
import { useInsertRoleAssignmentMutation } from '../../mutations/__generated__/insertRoleAssignment.generated';
import { useGetUserRolesByEmailLazyQuery } from '../../queries/__generated__/getUserRolesByEmail.generated';
import { currentContextAtom } from '../../state';
import TopAppBar from '../../common/components/TopAppBar';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import { RoutePaths } from '../../routes/AppRoutes';
import UserRoleSelect from './UserRoleSelect';
import CompanyAdminSwitch from './CompanyAdminSwitch';

interface DraftAccount {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRoleEnum;
  isAdmin: boolean;
}

export default function CompanyTeamCreateAccountForm(): React.JSX.Element {
  const { t } = useTranslation(['common', 'company', 'error', 'validation']);
  const navigate = useNavigate();
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const { companyId } = useAtomValue(currentContextAtom);
  const { control, formState, handleSubmit, setValue } = useForm<DraftAccount>();
  const [insertUser] = useInsertUserMutation();
  const [insertRoleAssignment] = useInsertRoleAssignmentMutation({});
  const [getUserByEmail] = useGetUserRolesByEmailLazyQuery();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const addRoles = async ({ role, isAdmin }: DraftAccount, userId: string): Promise<void> => {
    const insertRoles = [
      insertRoleAssignment({
        variables: {
          companyId,
          role,
          userId,
        },
      })
    ];

    if (isAdmin) {
      insertRoles.push(
        insertRoleAssignment({
          variables: {
            companyId,
            role: UserRoleEnum.CompanyAdmin,
            userId,
          },
        })
      );
    }

    const results = await Promise.all(insertRoles);

    if (results.some(({ errors }) => !!errors?.length)) {
      setError(t('error:an_error_occured'));
      throw new Error('Roles have not been added');
    }
  }

  const insertNewUser = async (draftAccount: DraftAccount): Promise<string | undefined> => {
    const { data } = await insertUser({
      variables: {
        data: {
          companyId,
          user: {
            first_name: draftAccount.firstName,
            last_name: draftAccount.lastName,
            email: draftAccount.email,
          },
        },
      },
    });

    if (!data?.insertUser?.userId) {
      setError(t('error:an_error_occured'));
      throw new Error('User has not been created');
    }

    return data?.insertUser?.userId;
  }

  const submitAccount = async (draftAccount: DraftAccount): Promise<void> => {
    const { data: getUserByEmailData } = await getUserByEmail({
      variables: {
        email: draftAccount.email,
        companyId,
      },
    });
    const userAlreadyInCompany = getUserByEmailData?.user[0]?.role_assignments?.length;
    let userId = getUserByEmailData?.user[0]?.id;

    if (userAlreadyInCompany) {
      setError(t('error:user_already_in_company'));
    } else {
      if (!userId) {
        userId = await insertNewUser(draftAccount);
      }

      await addRoles(draftAccount, userId!);

      setSuccess(true);
    }
  };

  useEffect((): void => {
    if (success) {
      setCommonSnackbarProps({
        label: t('company:account_created_successfully'),
        snackbarProps: {
          open: true,
        },
      });

      navigate(RoutePaths.COMPANY_TEAM);
    }
  }, [success]);

  useEffect((): void => {
    if (error) {
      setCommonSnackbarProps({
        label: error,
        snackbarProps: {
          open: true,
        },
        alertProps: {
          severity: 'error',
        },
      });
    }
  }, [error]);

  return (
    <>
      <TopAppBar
        configuration="small"
        title={t('company:create_account')}
        backTo={RoutePaths.ROOT}
        sx={{ display: 'flex' }}
      />
      <Box
        component="form"
        onSubmit={handleSubmit(submitAccount)}
        sx={{
          margin: 'auto',
          width: {
            sm: 600
          },
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          pt: 1,
          px: {
            xs: 2,
            sm: 0.5,
          },
        }}
      >
        <Box
          sx={{
            display: {
              xs: 'none',
              sm: 'flex',
            },
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <PersonAddAltIcon />
          <Typography variant="headlineSmall">
            {t('company:create_user_account')}
          </Typography>
        </Box>
        <Controller
          name="email"
          control={control}
          rules={{
            required: t('validation:this_field_is_required') as string,
            pattern: {
              value: /^\S+@\S+\.\S+$/,
              message: t('validation:email_format_is_invalid'),
            },
          }}
          render={({ field, fieldState }) => (
            <Box>
              <TextField
                {...field}
                fullWidth
                placeholder={t('common:email_address')}
                error={!!fieldState.error}
              />
              {fieldState.error
                && <FormHelperText error>{fieldState.error?.message}</FormHelperText>}
            </Box>
          )}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            gap: {
              xs: 3,
              sm: 1,
            },
          }}
        >
          <Controller
            name="lastName"
            control={control}
            rules={{
              required: t('validation:this_field_is_required') as string,
              pattern: {
                value: /^[\p{L}\s'-]+$/u,
                message: t('validation:only_name_characters')
              },
            }}
            render={({ field, fieldState }) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <TextField
                  {...field}
                  fullWidth
                  placeholder={t('common:name')}
                  error={!!fieldState.error}
                />
                {fieldState.error && <FormHelperText error>{fieldState.error?.message}</FormHelperText>}
              </Box>
            )}
          />
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: t('validation:this_field_is_required') as string,
              pattern: {
                value: /^[\p{L}\s'-]+$/u,
                message: t('validation:only_name_characters')
              },
            }}
            render={({ field, fieldState }) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <TextField
                  {...field}
                  fullWidth
                  placeholder={t('common:first_name')}
                  error={!!fieldState.error}
                />
                {fieldState.error && <FormHelperText error>{fieldState.error?.message}</FormHelperText>}
              </Box>
            )}
          />
        </Box>
        <Controller
          name="role"
          control={control}
          rules={{
            required: t('validation:this_field_is_required') as string,
          }}
          render={({ field, fieldState }) => (
            <Box>
              <UserRoleSelect onChange={field.onChange} value={field.value as unknown as User_Role_Enum} />
              {fieldState.error && <FormHelperText error>{fieldState.error?.message}</FormHelperText>}
            </Box>
          )}
        />
        <CompanyAdminSwitch onChange={(isAdmin) => setValue('isAdmin', isAdmin)} />
        <Divider sx={{ my: -1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'end', gap: 1.5 }}>
          <Button component={Link} to={RoutePaths.COMPANY_TEAM}>
            {t('common:cancel')}
          </Button>
          <Button variant="contained" disabled={formState.isSubmitted && !formState.isValid} type="submit">
            {t('company:create_the_account')}
          </Button>
        </Box>
      </Box>
    </>
  );
}
